import { Badge, Button } from "reactstrap";
import KaModal from "../../common/KaModal";
import { StagedOrderCompartment, StagedOrderDetails, StagedOrderStep } from "../../requests/useGetStagedOrders";

interface StagedOrderDetailsModalProps {
  stagedOrder: StagedOrderDetails;
  onClose: () => void;
  onLoad?: () => void;
}

const StagedOrderDetailsModal = (props: StagedOrderDetailsModalProps) => {
  const so = props.stagedOrder;

  const headerTitle = <span className="d-flex align-items-center gap-2 fs-4">
    Order {so.orderNumber}
    <Badge className={`bg-stagedOrderStep-${so.step}`}>{so.step.replace(/([A-Z])/g, ' $1').trim()}</Badge>
    {so.bay && <span className="position-absolute end-0 me-4 fw-semibold fs-5">{so.bay}</span>}
  </span>;

  return <KaModal
    isOpen={true}
    toggle={props.onClose}
    size="lg"
    fullscreen="md"
    scrollable
    contentClassName="h-100"
    title={headerTitle as any}
    body={<div className="d-flex flex-column h-100">
      <div className="flex-shrink-0">
        <div className="row g-0 small mb-1">
          <div className="col-12 col-md-6 pe-md-2">
            {so.orderNumbers.length > 0 && <InfoField icon="bi-file-ruled" label="Orders" value={so.orderNumbers.join(", ")} />}
            {so.releaseNumber && <InfoField icon="bi-file-text" label="Release #" value={so.releaseNumber} />}
            <InfoField icon="bi-clock-fill" label="Created" value={new Date(so.createdAt).toLocaleString()} />
          </div>
          <div className="col-12 col-md-6">
            <InfoField icon="bi-people" label="Customer" value={so.customers.join(", ")} />
            {so.destinations.some(d => d) && <InfoField icon="bi-geo-alt" label="Destination" value={so.destinations.filter(d => d).join(", ")} />}
          </div>
        </div>
        <div className="border-top pt-2 mt-4">
          {so.driverName && <div className="small">
            <span className="text-muted"><i className="bi-person-badge me-1" />Driver: </span>
            <span className="fw-semibold">{so.driverName} #{so.driverNumber}</span>
            {so.userEmail !== "Kiosk" && <span className="text-muted"> · {so.userEmail}</span>}
          </div>}
          {so.carrier && <div className="small">
            <span className="text-muted"><i className="bi-truck-front me-1" />Carrier: </span>
            <span className="fw-semibold">{so.carrier}</span>
          </div>}
          <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
            <span className="small">
              {so.transport && <>
                <span className="text-muted"><i className="bi-truck me-1" />Transport: </span>
                <span className="fw-semibold">{so.transport}</span>
              </>}
            </span>
            <span className="small">
              {so.tareWeight && <>
                <span className="text-muted">Tare</span> <span className="fw-semibold">{so.tareWeight}</span>
                <span className="mx-2 text-muted">·</span>
              </>}
              <span className="text-muted">Total Requested</span> <span className="fw-semibold">{so.requestedQuantity.toLocaleString()} lb</span>
            </span>
          </div>
        </div>
      </div>
      <div className="overflow-auto">
        <CompartmentCards compartments={so.compartments} />
      </div>
    </div>}
    footer={<div className="container">
      <div className="row justify-content-between">
        <Button color="secondary" size="lg" className="col-3" onClick={props.onClose}>Close</Button>
        {props.onLoad && so.step === StagedOrderStep.BayDirections &&
          <Button color="primary" size="lg" className="col-3" onClick={props.onLoad}><strong>Load</strong></Button>}
      </div>
    </div>} />
}

const CompartmentCards = ({ compartments }: { compartments: StagedOrderCompartment[] }) =>
  <div className="row row-cols-1 row-cols-sm-2 row-cols-md-1 row-cols-lg-2 g-2 mt-0 mx-0">
    {compartments.map((c, i) =>
      <div key={i} className="col">
        {c.products.length === 1
          ? <SingleProductCompartmentCard compartment={c} number={i + 1} />
          : <div className="border rounded h-100 px-3 py-2">
            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-baseline gap-1">
              <span className="text-muted text-uppercase small">Compartment {i + 1}</span>
              <span className="fw-bold fs-2 lh-1 text-nowrap">
                {c.weight.toLocaleString()}<span className="fs-6 text-muted fw-normal"> lb</span>
              </span>
            </div>
            <div className="mt-2">
              {c.products.map(p =>
                <div key={p.productName} className="d-flex justify-content-between fs-6 border-top py-1">
                  <span className="text-truncate pe-2">{p.productName}</span>
                  <span className="text-nowrap fw-semibold">{p.amount.toLocaleString()} lb</span>
                </div>)}
            </div>
          </div>}
      </div>)}
  </div>

const SingleProductCompartmentCard = ({ compartment, number }: { compartment: StagedOrderCompartment, number: number }) =>
  <div className="border rounded h-100 px-3 py-2 d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-2">
    <div className="overflow-hidden">
      <div className="text-muted text-uppercase small">Compartment {number}</div>
      <div className="text-truncate fw-semibold mt-1">{compartment.products[0].productName}</div>
    </div>
    <span className="fw-bold fs-2 lh-1 text-nowrap">{compartment.weight.toLocaleString()}<span className="fs-6 text-muted fw-normal"> lb</span></span>
  </div>

const InfoField = ({ icon, label, value }: { icon?: string, label: string, value: string }) =>
  <div className="text-truncate" title={value}>
    <span className="text-muted">{icon && <i className={`${icon} me-1`} />}{label}: </span>
    <span className="fw-semibold">{value}</span>
  </div>

export default StagedOrderDetailsModal;
